/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";

/**
 * Ported from bouncieweb's `components/AllFeatures.js` ("Everything Included").
 *
 * The original renders full-width (outside the page width container) with its own
 * internal `WidthConstraintContainer`, reproduced here with `wrap-legacy`.
 *
 * Original Emotion -> Tailwind mapping notes:
 *   - Wrapper: 2rem vertical padding, background from `mode`/`bgColor`. On the
 *     family page it is used with no props, so mode defaults to "light" ->
 *     blueLight8 (#E1EDFD) background, default text color.
 *   - `LandingPageHeaderH2` reproduced inline (display-extra-bold, responsive
 *     10vw / 7vw / 40 / 48, centered) with the section's mt-0 mb-8 overrides.
 *
 * Breakpoint map: xsm 431 -> min-[431px]:  sm 640 -> sm:  md 768 -> md:
 *   lg 1024 -> lg:  xlg 1280 -> xl:
 */

type Mode = "light" | "dark";

interface AllFeaturesProps {
  mode?: Mode;
  bgColor?: string;
  bottomContent?: React.ReactNode;
}

// ShortSentences: inline-block, no-wrap, small horizontal padding.
export function ShortSentences({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block px-1 whitespace-nowrap">{children}</span>
  );
}

// ListGroup: reproduces the original responsive column widths.
function ListGroup({
  heading,
  items,
}: {
  heading: string;
  items: readonly string[];
}) {
  return (
    <ul
      className={cn(
        "m-0 list-none whitespace-nowrap p-0 text-left ps-0",
        "w-[47%]",
        "min-[431px]:w-[40%]",
        "sm:w-[13rem]",
        "md:w-[14rem]",
        "lg:w-auto",
      )}
    >
      {/* ListHeader */}
      <h3 className="font-display mb-4 text-[20px] font-bold sm:text-[24px]">
        {heading}
      </h3>
      {items.map((item) => (
        // ListItem
        <li key={item} className="mb-2 text-[14.5px] sm:text-[18px]">
          {item}
        </li>
      ))}
    </ul>
  );
}

const COLUMNS: readonly { heading: string; items: readonly string[] }[] = [
  {
    heading: "GPS Location",
    items: ["Instant Updates", "Route Details", "Geo-Zones", "Trip History"],
  },
  {
    heading: "Driving",
    items: ["Speed Monitoring", "Speed Bands", "Driving Events", "Custom Alerts"],
  },
  {
    heading: "Safety",
    items: [
      "Accident Detection",
      "Accident Notification",
      "Vehicle Diagnostics",
      "Weather Alerts",
    ],
  },
  {
    heading: "More",
    items: [
      "Shared User Access",
      "Browser & App Access",
      "Online Reports",
      "Smart Integration",
    ],
  },
];

export default function AllFeatures({
  mode = "light",
  bgColor,
  bottomContent,
}: AllFeaturesProps) {
  const background =
    bgColor ?? (mode === "dark" ? colors.zachBlack : colors.blueLight8);

  return (
    // Wrapper: full-width band, 2rem vertical padding.
    <div
      className={cn("py-8", mode === "dark" && "text-white")}
      style={{ background }}
    >
      {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
      <div className="wrap-legacy">
        {/* LandingPageHeaderH2 with mt-0 mb-8 override. */}
        <h2
          className={cn(
            "font-display mt-0 mb-8 text-center font-extrabold",
            "text-[10vw] leading-[1.1em]",
            "min-[431px]:text-[7vw] sm:text-[40px] md:text-[48px]",
          )}
        >
          Everything Included
        </h2>

        {/* FeatureListFlex */}
        <div className="flex flex-wrap justify-center gap-4 lg:justify-between">
          {COLUMNS.map((col) => (
            <ListGroup
              key={col.heading}
              heading={col.heading}
              items={col.items}
            />
          ))}
        </div>

        {/* EmphasizedText */}
        <div className="text-center text-[16px] font-bold sm:text-[18px]">
          {bottomContent ?? (
            <>
              <ShortSentences>No contracts.</ShortSentences>
              <ShortSentences>No hidden fees.</ShortSentences>
              <ShortSentences>No activation fees.</ShortSentences>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
